import type { ReactElement, ChangeEvent, ReactNode } from 'react';
import React, { useEffect, useState } from 'react';
import Slider from 'rc-slider';
import styled from 'styled-components';
import { Inline, spacingHelper, Stack, TextInput } from '@construct-kit/core';
import {
  removeAllCommasFromNumber,
  fuzzyInputValueToStep,
  formatTextInputNumber,
} from './sliderInputUtil';

export type SliderInputProps = {
  icon?: ReactNode;
  filterRanges: number[];
  searchQueryRange: number[];
  textInputLabel?: string;
  onChange: (newPriceRange: number[]) => void;
};

const SliderWrapper = styled.div`
  margin: ${spacingHelper('large large 0 0')};
`;

const StyledTextInput = styled(TextInput)`
  flex-grow: inherit;
  width: 20.75rem;
`;

const backgroundColor = '#2B6ED2';
const borderColor = '#E9EBED';
const boxShadow = '0px 1px 4px rgba(0, 0, 0, 0.16)';
const sliderHandleStyle = {
  width: '1.75rem',
  height: '1.75rem',
  borderColor,
  transform: 'none',
  boxShadow,
  opacity: 'inherit',
  marginTop: '-0.75rem',
};

const SliderInput = ({
  icon,
  filterRanges,
  textInputLabel = '',
  searchQueryRange,
  onChange,
}: SliderInputProps): ReactElement => {
  const filterRangesWithDefault = [0, ...filterRanges, 0];
  const sliderMaxValue = filterRangesWithDefault.length - 1;

  const [filterInputValueRange, setFilterInputValueRange] = useState<number[]>(searchQueryRange);

  const [sliderRange, setSliderRange] = useState<number[]>([
    fuzzyInputValueToStep(searchQueryRange[0], filterRangesWithDefault),
    searchQueryRange[1] === 0
      ? sliderMaxValue
      : fuzzyInputValueToStep(searchQueryRange[1], filterRangesWithDefault),
  ]);

  useEffect(() => {
    setFilterInputValueRange(searchQueryRange);
    setSliderRange([
      fuzzyInputValueToStep(searchQueryRange[0], filterRangesWithDefault),
      searchQueryRange[1] === 0
        ? sliderMaxValue
        : fuzzyInputValueToStep(searchQueryRange[1], filterRangesWithDefault),
    ]);
  }, [searchQueryRange, filterRanges]);

  const filterInputMinValue = filterInputValueRange[0];
  const filterInputMaxValue = filterInputValueRange[1];

  const handleSliderChange = (value: number | number[]): void => {
    if (Array.isArray(value)) {
      const [minSliderStep, maxSliderStep] = value;
      if (sliderRange[0] !== minSliderStep) {
        setSliderRange(value);
        /*
          When slider inputs are on the same step, assign the max input value to the min input to avoid having min values larger than max values
        */
        const limitedFilterInputMinValue =
          minSliderStep === maxSliderStep
            ? filterInputMaxValue
            : filterRangesWithDefault[minSliderStep];
        onChange([limitedFilterInputMinValue, filterInputMaxValue]);
      } else {
        setSliderRange(value);
        const limitedFilterInputMaxValue =
          minSliderStep === maxSliderStep
            ? filterInputMinValue
            : filterRangesWithDefault[maxSliderStep];
        onChange([filterInputMinValue, limitedFilterInputMaxValue]);
      }
    }
  };

  const handleMinTextInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const minTextInputValue = removeAllCommasFromNumber(event.target.value);
    setFilterInputValueRange([minTextInputValue, filterInputMaxValue]);

    const minSliderRangeValue =
      minTextInputValue === 0
        ? 0
        : fuzzyInputValueToStep(minTextInputValue, filterRangesWithDefault);

    setSliderRange([minSliderRangeValue, sliderRange[1]]);
    onChange([minTextInputValue, filterInputMaxValue]);
  };

  const handleMaxTextInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const maxTextInputValue = removeAllCommasFromNumber(event.target.value);
    setFilterInputValueRange([filterInputMinValue, maxTextInputValue]);

    const maxSliderRangeValue =
      maxTextInputValue === 0
        ? sliderMaxValue
        : fuzzyInputValueToStep(maxTextInputValue, filterRangesWithDefault);

    setSliderRange([sliderRange[0], maxSliderRangeValue]);
    onChange([filterInputMinValue, maxTextInputValue]);
  };

  const isInputValueInRange = (inputValue: number): boolean =>
    inputValue >= filterRangesWithDefault[1] &&
    inputValue <= filterRangesWithDefault[filterRangesWithDefault.length - 2];

  const handleMinTextInputOnBlur = (event: ChangeEvent<HTMLInputElement>): void => {
    const minTextInputValue = removeAllCommasFromNumber(event.target.value);

    if (
      !isInputValueInRange(minTextInputValue) ||
      (filterInputMaxValue !== 0 && minTextInputValue > filterInputMaxValue)
    ) {
      const isZeroOrOutOfRange = minTextInputValue === 0 || !isInputValueInRange(minTextInputValue);
      const minInputValue = isZeroOrOutOfRange ? 0 : filterInputMaxValue;
      setSliderRange([isZeroOrOutOfRange ? 0 : sliderRange[1], sliderRange[1]]);
      setFilterInputValueRange([minInputValue, filterInputMaxValue]);
      onChange([minInputValue, filterInputMaxValue]);
    }
  };

  const handleMaxTextInputOnBlur = (event: ChangeEvent<HTMLInputElement>): void => {
    const maxTextInputValue = removeAllCommasFromNumber(event.target.value);

    if (
      !isInputValueInRange(maxTextInputValue) ||
      (filterInputMinValue !== 0 && maxTextInputValue < filterInputMinValue)
    ) {
      const isZeroOrOutOfRange = maxTextInputValue === 0 || !isInputValueInRange(maxTextInputValue);
      const maxInputValue = isZeroOrOutOfRange ? 0 : filterInputMinValue;
      setSliderRange([sliderRange[0], isZeroOrOutOfRange ? sliderMaxValue : sliderRange[0]]);
      setFilterInputValueRange([filterInputMinValue, maxInputValue]);
      onChange([filterInputMinValue, maxInputValue]);
    }
  };

  return (
    <Stack inset="0" gap="0">
      <SliderWrapper>
        <Slider
          range
          step={1}
          allowCross={false}
          trackStyle={{ backgroundColor }}
          handleStyle={sliderHandleStyle}
          onChange={(value: number | number[]) => handleSliderChange(value)}
          value={sliderRange}
          min={0}
          max={sliderMaxValue}
        />
      </SliderWrapper>
      <Inline gap="large" justifyContent="space-between" inset="medium 0 0 0">
        <StyledTextInput
          iconLeft={icon}
          sizeVariant="large"
          label={`Min ${textInputLabel}`}
          value={filterInputMinValue === 0 ? '' : `${formatTextInputNumber(filterInputMinValue)}`}
          onBlur={handleMinTextInputOnBlur}
          onChange={handleMinTextInputChange}
          placeholder="Any"
        />
        <StyledTextInput
          iconLeft={icon}
          sizeVariant="large"
          label={`Max ${textInputLabel}`}
          value={filterInputMaxValue === 0 ? '' : `${formatTextInputNumber(filterInputMaxValue)}`}
          onBlur={handleMaxTextInputOnBlur}
          onChange={handleMaxTextInputChange}
          placeholder="Any"
        />
      </Inline>
    </Stack>
  );
};
export default SliderInput;
